import React, { useState, useEffect, useMemo } from "react";
import type {
  IAssessmentDetailsStepProps,
  IAssessmentFormData,
} from "./definitions";
import styles from "./style.module.scss";
import {
  Box,
  Checkbox,
  Container,
  FormField,
  Header,
  SpaceBetween,
  Spinner,
  Alert,
} from "@amzn/awsui-components-react/polaris";
import {
  ControlledSelect,
  ControlledTextarea,
  ControlledDatePicker,
} from "@amzn/react-hook-form-polaris";
import { get, useFormState } from "react-hook-form";
import MonetaryInput from "components/monetary-input/MonetaryInput";
import { useGetScanResultQuery } from "pages/draft-overview/endpoints";
import { useGaiaObjectUiContent } from "features/ui-content/hooks";
import {
  useBreakdownState,
  calculateAssessmentTotal,
  makeUpdateAmount,
  parseBreakdownsFromScanResult,
} from "./utils";
import { transformScanResultToFormData } from "components/create-edit-gaiaobject-form/util";
import { CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME } from "models/generateCustomAttributesSchema";
import { useLocalStorage } from "hooks/useLocalStorage";
import WizardFilesSection from "components/wizard-files-section";
import OutcomeBreakdownTable from "components/outcome-breakdown-table";
import { TaxYearBreakdown } from "models/schemas";

const AssessmentDetailsStep: React.FC<IAssessmentDetailsStepProps> = ({
  shortIdForScanJob,
  currencySign,
  isScanWizard = false,
  externalFormData,
  onFormDataChange,
  taxAreaOptions = [],
  selectedAssessmentId,
  gaiaObjectId,
  isCreateMode,
  category,
  stagedFiles = [],
  onStagedFilesChange,
  control,
  setValue,
}) => {
  const formState = useFormState({ control });
  const {
    data: scanResultData,
    isLoading,
    error,
  } = useGetScanResultQuery(
    { shortId: shortIdForScanJob! },
    { skip: !isScanWizard || !shortIdForScanJob }
  );

  const uiContent: any = useGaiaObjectUiContent(category);
  const storageKey = `assessment-step-${shortIdForScanJob}`;

  const [internalFormData, setInternalFormData] =
    useLocalStorage<IAssessmentFormData>(storageKey, {
      dateReceived: "",
      type: isScanWizard ? "Preliminary" : "",
      notes: "",
      amounts: {
        totalTaxAmount: "0",
        totalPenaltyAmount: "0",
        totalInterestAmount: "0",
        totalOtherAmount: "0",
      },
      breakdowns: {
        byTaxYear: false,
        byTaxArea: false,
        byTaxYearArea: false,
      },
    });

  const formData =
    !isScanWizard && externalFormData ? externalFormData : internalFormData;
  const setFormData =
    !isScanWizard && onFormDataChange ? onFormDataChange : setInternalFormData;

  const [parsedBreakdowns, setParsedBreakdowns] = useState<TaxYearBreakdown[]>(
    []
  );

  const {
    state: breakdownState,
    handlers: breakdownHandlers,
    disabledStates,
  } = useBreakdownState(taxAreaOptions.length > 0);

  useEffect(() => {
    if (formData.breakdowns) {
      const hasChanged =
        breakdownState.byTaxYear !== formData.breakdowns.byTaxYear ||
        breakdownState.byTaxArea !== formData.breakdowns.byTaxArea ||
        breakdownState.byTaxYearArea !== formData.breakdowns.byTaxYearArea;

      if (hasChanged) {
        if (formData.breakdowns.byTaxYear)
          breakdownHandlers.handleTaxYearChange(true);
        if (formData.breakdowns.byTaxArea)
          breakdownHandlers.handleTaxAreaChange(true);
        if (formData.breakdowns.byTaxYearArea)
          breakdownHandlers.handleTaxYearAreaChange(true);
      }
    }
  }, [
    formData.breakdowns?.byTaxYear,
    formData.breakdowns?.byTaxArea,
    formData.breakdowns?.byTaxYearArea,
  ]);

  useEffect(() => {
    // Only update if breakdowns actually changed to prevent infinite loop
    if (
      formData.breakdowns?.byTaxYear !== breakdownState.byTaxYear ||
      formData.breakdowns?.byTaxArea !== breakdownState.byTaxArea ||
      formData.breakdowns?.byTaxYearArea !== breakdownState.byTaxYearArea
    ) {
      setFormData({
        ...formData,
        breakdowns: breakdownState,
      });
    }
  }, [breakdownState, formData.breakdowns]);

  const handleFilesChange = onStagedFilesChange || (() => {});

  const updateAmount = makeUpdateAmount(formData, setFormData, control);

  useEffect(() => {
    if (scanResultData?.scanResult) {
      const scanFormData = transformScanResultToFormData(
        scanResultData.scanResult as any,
        [],
        uiContent
      );
      const data = scanFormData[CUSTOM_ATTRIBUTES_DATA_MAP_KEY_NAME] || {};
      const hasStoredData =
        formData.amounts.totalTaxAmount &&
        formData.amounts.totalTaxAmount !== "0";

      const breakdowns = parseBreakdownsFromScanResult(
        scanResultData.scanResult
      );
      setParsedBreakdowns(breakdowns);

      if (!hasStoredData) {
        setFormData({
          ...formData,
          dateReceived: data.noticeDate || formData.dateReceived,
          notes: scanResultData.scanResult.notes || formData.notes,
          parsedBreakdowns: breakdowns,
          amounts: {
            totalTaxAmount: data.totalTaxAmount || "0",
            totalPenaltyAmount: data.totalPenaltyAmount || "0",
            totalInterestAmount: data.totalInterestAmount || "0",
            totalOtherAmount: data.totalOtherAmount || "0",
          },
        });
      }
    }
  }, [scanResultData, uiContent]);

  // Sync formData with react-hook-form to display values in UI
  useEffect(() => {
    if (!setValue) return;

    const fieldsToSync = {
      dateReceived: formData.dateReceived,
      type: formData.type,
      notes: formData.notes,
    };

    Object.entries(fieldsToSync).forEach(([field, value]) => {
      if (value) setValue(field as any, value);
    });
  }, [formData.dateReceived, formData.type, formData.notes, setValue]);

  const total = useMemo(() => {
    return calculateAssessmentTotal({
      tax: formData.amounts.totalTaxAmount,
      penalty: formData.amounts.totalPenaltyAmount,
      interest: formData.amounts.totalInterestAmount,
      other: formData.amounts.totalOtherAmount,
    });
  }, [formData.amounts]);

  const assessmentTypeOptions = uiContent?.assessmentTypeOptions || [];

  if (isLoading) {
    return (
      <Container>
        <div className={styles.loadingContainer}>
          <Spinner size="large" />
          <div className={styles.loadingText}>Loading assessment data...</div>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert type="error" header="Error loading assessment data">
          Unable to load scan result data. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <SpaceBetween direction="vertical" size="l">
      <Container>
        <SpaceBetween direction="vertical" size="m">
          <div className={styles.firstRowWrapper}>
            <div className={styles.dateCustomPicker}>
              <FormField
                label="Date received"
                errorText={get(formState.errors, "dateReceived.message")}
              >
                <ControlledDatePicker
                  name="dateReceived"
                  control={control}
                  placeholder="YYYY/MM/DD"
                  onChange={({ detail }) => {
                    setFormData({
                      ...formData,
                      dateReceived: detail.value,
                    });
                  }}
                />
              </FormField>
            </div>
            <div className={styles.typeBox}>
              <FormField
                label="Type"
                errorText={get(formState.errors, "type.message")}
              >
                <ControlledSelect
                  name="type"
                  options={assessmentTypeOptions}
                  control={control}
                  placeholder="Select assessment type"
                  onChange={({ detail }) => {
                    setFormData({
                      ...formData,
                      type: detail.selectedOption.value || "",
                    });
                  }}
                />
              </FormField>
            </div>
          </div>
          <div className={styles.notesBox}>
            <FormField
              label="Notes"
              errorText={get(formState.errors, "notes.message")}
            >
              <ControlledTextarea
                name="notes"
                control={control}
                placeholder=""
                rows={3}
                onChange={({ detail }) => {
                  setFormData({
                    ...formData,
                    notes: detail.value,
                  });
                }}
              />
            </FormField>
          </div>
        </SpaceBetween>
      </Container>
      {!isScanWizard && (
        <WizardFilesSection
          parentId={selectedAssessmentId}
          gaiaObjectId={gaiaObjectId || ""}
          category="assessments"
          stagedFiles={stagedFiles}
          onStagedFilesChange={handleFilesChange}
          isCreateMode={isCreateMode || false}
        />
      )}
      <Container
        header={
          <div className={styles.headerContainer}>
            <Header variant="h2">Assessment overview</Header>
          </div>
        }
      >
        <div className={styles.overviewWrapper}>
          <FormField
            label="Tax"
            controlId="totalTaxAmount"
            errorText={get(formState.errors, "totalTaxAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalTaxAmount}
              onChange={(value) => updateAmount("totalTaxAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Penalty"
            controlId="totalPenaltyAmount"
            errorText={get(formState.errors, "totalPenaltyAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalPenaltyAmount}
              onChange={(value) => updateAmount("totalPenaltyAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Interest"
            controlId="totalInterestAmount"
            errorText={get(formState.errors, "totalInterestAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalInterestAmount}
              onChange={(value) => updateAmount("totalInterestAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <FormField
            label="Other"
            controlId="totalOtherAmount"
            errorText={get(formState.errors, "totalOtherAmount.message")}
          >
            <MonetaryInput
              title=""
              value={formData.amounts.totalOtherAmount}
              onChange={(value) => updateAmount("totalOtherAmount", value)}
              currencySign={currencySign}
            />
          </FormField>
          <div className={styles.dollarInput}>
            <FormField label="Total">
              <div className={styles.inputContainer}>
                <span className={styles.dollarSign}> {currencySign} </span>
                <input
                  value={total}
                  readOnly={true}
                  className={styles.inputField}
                />
              </div>
            </FormField>
          </div>
        </div>

        {!isScanWizard && (
          <>
            <br />
            <Box>
              <SpaceBetween direction="vertical" size="s">
                <div>
                  <Header variant="h3">Further breakdown the amount by</Header>
                </div>
                <div>
                  <SpaceBetween direction="vertical" size="xs">
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxYearChange(detail.checked)
                      }
                      checked={breakdownState.byTaxYear}
                      disabled={disabledStates.taxYear}
                    >
                      Tax year
                    </Checkbox>
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxAreaChange(detail.checked)
                      }
                      checked={breakdownState.byTaxArea}
                      disabled={disabledStates.taxArea}
                      description={
                        taxAreaOptions.length === 0 ? (
                          <i>No active tax areas</i>
                        ) : (
                          ""
                        )
                      }
                    >
                      Tax area
                    </Checkbox>
                    <Checkbox
                      onChange={({ detail }) =>
                        breakdownHandlers.handleTaxYearAreaChange(
                          detail.checked
                        )
                      }
                      checked={breakdownState.byTaxYearArea}
                      disabled={disabledStates.taxYearArea}
                      description={
                        taxAreaOptions.length === 0 ? (
                          <i>No active tax areas</i>
                        ) : (
                          ""
                        )
                      }
                    >
                      Tax year & area
                    </Checkbox>
                  </SpaceBetween>
                </div>
              </SpaceBetween>
            </Box>
          </>
        )}
      </Container>
      {parsedBreakdowns.length > 0 && (
        <OutcomeBreakdownTable
          breakdownType="Tax year"
          overviewType="Assessment"
          outcomeBreakdowns={parsedBreakdowns}
          currencySign={currencySign}
        />
      )}
    </SpaceBetween>
  );
};

export default AssessmentDetailsStep;
